import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Goal {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  target_value: number;
  current_value: number;
  target_date?: string;
  status: 'active' | 'completed' | 'paused' | 'cancelled';
  category: 'revenue' | 'growth' | 'efficiency' | 'customer' | 'general';
  unit: string;
  created_at: string;
  updated_at: string;
}

export const useGoals = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGoals([]);
        return;
      }

      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGoals((data || []) as Goal[]);
    } catch (err) {
      console.error('Error fetching goals:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch goals');
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const updateGoalProgress = async (goalId: string, currentValue: number) => {
    try {
      const { error } = await supabase
        .from('goals')
        .update({ 
          current_value: currentValue,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);

      if (error) throw error;
      
      await fetchGoals(); // Refresh goals
      toast.success('Goal progress updated!');
    } catch (err) {
      console.error('Error updating goal:', err);
      toast.error('Failed to update goal progress');
    }
  };

  const deleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (error) throw error;
      
      await fetchGoals(); // Refresh goals
      toast.success('Goal deleted successfully!');
    } catch (err) {
      console.error('Error deleting goal:', err);
      toast.error('Failed to delete goal');
    }
  };

  const markGoalComplete = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId);

      if (error) throw error;
      
      await fetchGoals(); // Refresh goals
      toast.success('Goal marked as completed!');
    } catch (err) {
      console.error('Error completing goal:', err);
      toast.error('Failed to complete goal');
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  return {
    goals,
    loading,
    error,
    fetchGoals,
    updateGoalProgress,
    deleteGoal,
    markGoalComplete
  };
};